/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import * as loginActions from "../../../store/login/actions";
import CheckmarkIcon from "@mui/icons-material/DoneRounded";
import { getLpByLocation } from "../../../services/landingPagesList";
import Typography from "@mui/material/Typography";
import { FormProvider } from "../../../provider/Form/FormProvider";
import DivorceCheckinForm from "./DivorceCheckinForm";
import { useLocation, useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "../../../services/reactQuery/reactQueryService";
import divorceCheckinStyle from "./divorceCheckinStyle";
import { Box } from "@mui/material";
import { getPartnerNameByIri } from "../../../constants/partnerIris";
import type { PartnerIri } from "../../../constants/partnerIris";

type DivorceCheckinProps = {
  idPrefix: string;
  color?: "default" | "grey";
  checkinHeading?: string;
  bullet1?: string;
  bullet2?: string;
  bullet3?: string;
  target?: string;
  partner?: PartnerIri | null;
};

export default function DivorceCheckin({
  color = "default",
  bullet1 = "Kostenloses Erstgespräch",
  bullet2 = "Scheidungskosten berechnen",
  bullet3 = "Persönlicher Scheidungsplaner",
  target = "/servicewelt",
  idPrefix,
  checkinHeading = "Wir führen Sie Schritt für Schritt durch Ihre Scheidung",
  partner = null,
}: DivorceCheckinProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const initialValues = {
    telephone: "",
    email: "",
    gender: "",
    familyName: "",
    givenName: "",
    classification: "",
  };

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);
    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.email);
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);
    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      telephone: values.telephone,
      person: {
        gender: values.gender,
        givenName: values.givenName,
        familyName: values.familyName,
      },
      prelead: {
        initialProduct: "divorce",
        classification: values.classification,
        landingPage: landingPage.keyword,
        partner: partnerName,
      },
    };

    await loginActions.register(data);
    await createMutation.mutateAsync({
      uri: "/divorces",
      data: {
        acquisitionPartner: partner,
        affiliateCode: localStorage.getItem("gclid"),
        landingPage: landingPage.keyword,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={[divorceCheckinStyle.formPaper, divorceCheckinStyle[color]]}>
      <Box id={idPrefix + "-checkin"} sx={divorceCheckinStyle.scrollToAnchor} />
      <Typography variant={"h4"} sx={divorceCheckinStyle.heading}>
        {checkinHeading}
      </Typography>
      <Grid container>
        <Grid size={12}>
          <Grid container sx={divorceCheckinStyle.advantagesGridContainer}>
            <Grid
              sx={divorceCheckinStyle.advantagesGridItems}
              size={{
                sm: 6,
                xs: 12,
                md: 4
              }}>
              <Box sx={divorceCheckinStyle.advantagesContainer}>
                <CheckmarkIcon sx={divorceCheckinStyle.checkmark} />
                <Grid component={"span"} sx={divorceCheckinStyle.advantages}>
                  {bullet1}
                </Grid>
              </Box>
            </Grid>
            <Grid
              sx={divorceCheckinStyle.advantagesGridItems}
              size={{
                sm: 6,
                xs: 12,
                md: 4
              }}>
              <Box sx={divorceCheckinStyle.advantagesContainer}>
                <CheckmarkIcon sx={divorceCheckinStyle.checkmark} />
                <Grid component={"span"} sx={divorceCheckinStyle.advantages}>
                  {bullet2}
                </Grid>
              </Box>
            </Grid>
            <Grid
              sx={divorceCheckinStyle.advantagesGridItems}
              size={{
                sm: 6,
                xs: 12,
                md: 4
              }}>
              <Box sx={divorceCheckinStyle.advantagesContainer}>
                <CheckmarkIcon sx={divorceCheckinStyle.checkmark} />
                <Grid component={"span"} sx={divorceCheckinStyle.advantages}>
                  {bullet3}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <DivorceCheckinForm isLoading={isLoading} emailErrorText={emailErrorText} setEmailErrorText={setEmailErrorText} />
      </FormProvider>
    </Paper>
  );
}
